'use client';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import Pagination from '../../atoms/pagination';
import List from './components/list';
import DesktopFilters from './components/desktop-filters';
import Sort from './components/sort';
import ActiveFilters from './components/active-filters';
import Link from '@/components/atoms/link';
import Breadcrumb from '@/components/molecules/breadcrumb';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import Button from '@/components/atoms/button';
import { sdk } from '@/sdk/CommercetoolsSDK';
import Spinner from '../spinner';


interface VideoItem {
  idProduct: string;
  idItem: string;
  category: string;
  idVideo: string;
  videoName: string;
  videoUrl: string;
  videoType: string;
  videoThumbnail: string;
  description: string;
  videoDuration: string;
  dateCreated: string;
  dateUpdated: string;
  variantKey: string;
}

interface VideoListProps {
  data: {
    totalCount: number;
    videoList: VideoItem[];
    message: string | null;
    statusCode: string;
    errorMessage: string | null;
  };
}

const VideoList = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(24);
  const pathname = usePathname();
  const [data, setData] = useState<VideoListProps['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const extensions = sdk.composableCommerce;


  useEffect(() => {
    async function fetchVideoList() {
      const regex = /Calling extension(?: \S+)? failed/;
      const maxRetries = 3;
      const retryDelay = 1000; // 1 second delay between retries

      try {
        setLoading(true);

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const response = await extensions.custom.videoList({});
            // Update the data state with the response
            if (response?.data?.data) {
              setData(response?.data?.data);
            }
            break; // Success - exit retry loop
          } catch (error: any) {
            const errorMessage = error?.message || error?.toString() || '';

            // Check if error matches the retry pattern
            if (regex.test(errorMessage) && attempt < maxRetries - 1) {
              console.warn(`Attempt ${attempt + 1} failed with extension error. Retrying...`);
              // Wait before retrying
              await new Promise(resolve => setTimeout(resolve, retryDelay * (attempt + 1)));
              continue;
            }

            // If it's not a retryable error or we've exhausted retries, throw
            throw error;
          }
        }
      } catch (error) {
        console.error('Error fetching video list:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchVideoList();
  }, []);

  // Seed for deterministic initial shuffle
  const shuffleSeedRef = useRef<number>(Math.floor(Math.random() * 1_000_000_000));

  // Mulberry32 seeded RNG
  const mulberry32 = (seed: number) => {
    let t = seed >>> 0;
    return () => {
      t += 0x6D2B79F5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  };

  // Deterministic shuffle
  const shuffleDeterministic = (arr: VideoItem[], seed: number) => {
    const a = [...arr];
    const rand = mulberry32(seed);
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(rand() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  };

  // Initialize filters from URL or use defaults
  // Default sortBy to empty string => initial list will be shuffled
  type FilterState = {
    category: string[];
    type: string[];
    sortBy: string;
  };

  const parseMulti = (param: string | null): string[] => {
    if (!param) return [];
    return param
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const [filters, setFilters] = useState<FilterState>({
    category: parseMulti(searchParams.get('category')),
    type: parseMulti(searchParams.get('type')),
    sortBy: searchParams.get('sortBy') || '',
  });

  // handlers
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.category.length > 0) params.set('category', filters.category.join(','));
    if (filters.type.length > 0) params.set('type', filters.type.join(','));
    // Include sortBy in URL when it is set (including 'newest')
    if (filters.sortBy) {
      params.set('sortBy', filters.sortBy);
    }

    // Update URL without page reload
    const queryString = params.toString();
    const newUrl = `${window.location.pathname}${queryString ? `?${queryString}` : ''}`;
    window.history.replaceState({ ...history.state }, '', newUrl);
  }, [filters]);

  // Toggle a filter value on/off for multi-select filters
  const toggleFilter = (
    name: 'category' | 'type',
    value: string,
    checked: boolean
  ) => {
    setFilters((prev) => {
      const current = new Set(prev[name]);
      if (checked) {
        current.add(value);
      } else {
        current.delete(value);
      }
      return { ...prev, [name]: Array.from(current) } as FilterState;
    });
  };

  const setSortBy = (value: string) => {
    setFilters((prev) => ({ ...prev, sortBy: value }));
  };

  const clearFilter = (name: 'category' | 'type', value?: string) => {
    if (value) {
      // Remove a single value
      setFilters((prev) => ({
        ...prev,
        [name]: prev[name].filter((v) => v !== value),
      }));
    } else {
      // Clear all for that name
      setFilters((prev) => ({ ...prev, [name]: [] }));
    }
  };

  const resetFilters = () => {
    // Reset filters and clear URL params
    setFilters({
      category: [],
      type: [],
      sortBy: '', // back to shuffled default
    });
    // Reset URL to path without query params
    window.history.replaceState({}, '', window.location.pathname);
  };

  // unique categories and types
  const categories = useMemo(() => {
    if (!data?.videoList) return [];
    return [...new Set(data.videoList.map((video) => video.category))].sort();
  }, [data?.videoList]);

  const types = useMemo(() => {
    if (!data?.videoList) return [];
    return [...new Set(data.videoList.map((video) => video.videoType))].sort();
  }, [data?.videoList]);

  // sort options
  const sortOptions = useMemo(
    () => [
      { value: 'newest', label: 'New' },
      { value: 'name-asc', label: 'Name A-Z' },
      { value: 'name-desc', label: 'Name Z-A' },
    ],
    []
  );

  // Get unique videos by removing duplicates based on idVideo
  const uniqueVideos = useMemo(() => {
    if (!data?.videoList) return [] as VideoItem[];

    const unique = Array.from(new Map(
      data.videoList.map(video => [video.idVideo, video])
    ).values());

    // Log duplicate information
    if (unique.length < data.videoList.length) {
      console.log(`Removed ${data.videoList.length - unique.length} duplicate videos`);
      console.log('Unique video count:', unique.length);
    }

    return unique;
  }, [data?.videoList]);

  // Precompute a deterministically shuffled master list for initial (no sort) view
  const shuffledMaster = useMemo(() => {
    return shuffleDeterministic(uniqueVideos, shuffleSeedRef.current);
  }, [uniqueVideos]);

  // filtered & sorted videos
  const filteredVideos = useMemo(() => {
    if (!uniqueVideos.length) return [];

    let base = filters.sortBy ? [...uniqueVideos] : [...shuffledMaster];

    // Apply filters (preserving base order)
    if (filters.category.length > 0) {
      base = base.filter((video) => filters.category.includes(video.category));
    }
    if (filters.type.length > 0) {
      base = base.filter((video) => filters.type.includes(video.videoType));
    }

    // Apply sorting when selected
    if (filters.sortBy) {
      switch (filters.sortBy) {
        case 'newest':
          base.sort(
            (a, b) =>
              new Date(b.dateCreated).getTime() -
              new Date(a.dateCreated).getTime()
          );
          break;
        case 'name-asc':
          base.sort((a, b) => a.videoName.localeCompare(b.videoName));
          break;
        case 'name-desc':
          base.sort((a, b) => b.videoName.localeCompare(a.videoName));
          break;
      }
    }

    return base;
  }, [uniqueVideos, shuffledMaster, filters.category, filters.type, filters.sortBy]);

  // Per-filter counts for all options (respecting other active filters)
  const countsByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    if (!uniqueVideos.length) return map;
    // Apply non-category filters (type) first
    const base = uniqueVideos.filter((v) =>
      filters.type.length > 0 ? filters.type.includes(v.videoType) : true
    );
    for (const cat of categories) {
      map[cat] = base.filter((v) => v.category === cat).length;
    }
    return map;
  }, [uniqueVideos, categories, filters.type]);

  const countsByType = useMemo(() => {
    const map: Record<string, number> = {};
    if (!uniqueVideos.length) return map;
    // Apply non-type filters (category) first
    const base = uniqueVideos.filter((v) =>
      filters.category.length > 0 ? filters.category.includes(v.category) : true
    );
    for (const t of types) {
      map[t] = base.filter((v) => v.videoType === t).length;
    }
    return map;
  }, [uniqueVideos, types, filters.category]);

  // Get current videos
  const indexOfLastVideo = currentPage * itemsPerPage;
  const indexOfFirstVideo = indexOfLastVideo - itemsPerPage;
  const currentVideos = filteredVideos.slice(indexOfFirstVideo, indexOfLastVideo);

  // New state for loaded videos
  const [loadedVideos, setLoadedVideos] = useState<VideoItem[]>([]);

  useEffect(() => {
    // Initialize loadedVideos with the first page of videos
    setLoadedVideos(filteredVideos.slice(0, currentPage * itemsPerPage));
  }, [filteredVideos, currentPage]);

  // Change page
  const paginate = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Update URL with page number
    const params = new URLSearchParams();
    if (filters.category.length > 0) params.set('category', filters.category.join(','));
    if (filters.type.length > 0) params.set('type', filters.type.join(','));
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    params.set('page', pageNumber.toString());
    router.push(`${pathname}?${params.toString()}`, { scroll: false });
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters.category, filters.type, filters.sortBy]);

  if (loading) {
    return (
      <>
        <Spinner />
        <div className="overlay fixed left-0 top-0 z-[1002] h-full lg:w-full w-96 lg:bg-gray-800 bg-[f7f7f7] opacity-75"></div>
      </>
    );
  }

  if (!data?.videoList?.length) {
    return <div className="text-center py-12">No videos found</div>;
  }

  return (
    <>
      <div className="hidden pt-12 md:!block lg:px-10 px-4">
        <Breadcrumb Separator={<ChevronRightIcon className="w-4" />} className="">
          <Link link={'/'}>
            <span className="text-12 !font-normal capitalize leading-6 text-black">
              Home
            </span>
          </Link>
          <span className="text-12 !font-normal capitalize leading-6 text-black">
            Video Library
          </span>
        </Breadcrumb>
      </div>
      <div className="mt-10 text-center lg:px-10 px-4 text-23 font-medium leading-[55px] lg:text-48 lg:leading-8 lg:font-semibold">Video Library</div>
      <div className="mt-5 lg:mt-[4.5rem] bg-[#F3EDE9] pb-12 md:mt-10 lg:grid lg:grid-cols-[15rem_1fr] lg:px-12 xl:gap-6 xl:pb-[72px] 2xl:px-20">
        {/* Desktop Filters */}
        <DesktopFilters
          categories={categories}
          types={types}
          filters={filters}
          onToggleFilter={toggleFilter}
          onClear={resetFilters}
          value={filters.sortBy}
          options={sortOptions}
          onChange={(v) => setSortBy(v)}
          filter={{ category: filters.category, type: filters.type }}
          filteredCount={filteredVideos.length}
          onClearAll={clearFilter}
          countsByCategory={countsByCategory}
          countsByType={countsByType}
        />

        {/* Main Content */}
        <div className="p-4 pt-4 lg:grow lg:pt-[62px]">
          <div className="hidden items-center justify-between lg:flex">
            <div className="div">
              <ActiveFilters
                filters={{ category: filters.category, type: filters.type }}
                filteredCount={filteredVideos.length}
                onClear={clearFilter}
                countsByCategory={countsByCategory}
                countsByType={countsByType}
              />
            </div>
            <Sort
              value={filters.sortBy}
              options={sortOptions}
              onChange={(v) => setSortBy(v)}
            />
          </div>

          <div className="md:mt-3">
            <List data={{ videoList: loadedVideos }} />
          </div>
          {filteredVideos.length > 0 && (
            <>
              <div className="mt-11 hidden lg:block">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredVideos.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </div>

              {loadedVideos.length < filteredVideos.length && ( // Check if there are more videos to load
                <div className="block lg:hidden mt-11">
                  <Button
                    className="w-full border border-[#2B2C39] text-white !bg-[#2B2C39] px-9 py-3 text-16 font-medium"
                    onClick={() => {
                      if (currentPage * itemsPerPage < filteredVideos.length) {
                        setCurrentPage((prevPage) => prevPage + 1);
                        setLoadedVideos((prevVideos) => [
                          ...prevVideos,
                          ...filteredVideos.slice(
                            prevVideos.length,
                            prevVideos.length + itemsPerPage
                          ),
                        ]);
                      }
                    }}
                    disabled={currentPage * itemsPerPage >= filteredVideos.length}
                  >
                    Show More
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoList;
